import React from 'react';
import { RotateCcw, X } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';

interface InheritedTagChipsProps {
  testIdPrefix: string;
  inheritedTags: string[];
  suppressedTags: string[];
  getLabel: (tag: string) => string;
  getColor?: (tag: string) => string | undefined;
  onRemove: (tag: string) => void;
  onRestore: (tag: string) => void;
}

export const InheritedTagChips: React.FC<InheritedTagChipsProps> = ({
  testIdPrefix,
  inheritedTags,
  suppressedTags,
  getLabel,
  getColor,
  onRemove,
  onRestore,
}) => {
  const { t } = useTranslation('genogram');

  if (inheritedTags.length === 0 && suppressedTags.length === 0) return null;

  const renderDot = (tag: string) => {
    const color = getColor?.(tag);
    if (!color) return null;
    return <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: color }} aria-hidden="true" />;
  };

  return (
    <div className="space-y-1.5" data-testid={`${testIdPrefix}.inherited`}>
      <p className="text-[11px] text-muted-foreground">{t('inheritance.inheritedLabel', 'Inherited from parents')}</p>
      <div className="flex flex-wrap gap-1">
        {inheritedTags.map(tag => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 rounded-md border border-dashed bg-muted px-1.5 py-0.5 text-xs text-muted-foreground"
          >
            {renderDot(tag)}
            <span>{getLabel(tag)}</span>
            <button
              type="button"
              className="rounded-sm opacity-70 hover:opacity-100"
              onClick={() => onRemove(tag)}
              title={t('inheritance.removeInherited', 'Remove inherited tag from this person')}
              aria-label={t('inheritance.removeInherited', 'Remove inherited tag from this person')}
              data-testid={`${testIdPrefix}.inherited.remove.${tag}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        {suppressedTags.map(tag => (
          <span
            key={tag}
            className={cn(
              'inline-flex items-center gap-1 rounded-md border border-dashed px-1.5 py-0.5 text-xs text-muted-foreground',
              'opacity-60',
            )}
          >
            {renderDot(tag)}
            <span className="line-through">{getLabel(tag)}</span>
            <button
              type="button"
              className="rounded-sm opacity-70 hover:opacity-100"
              onClick={() => onRestore(tag)}
              title={t('inheritance.restoreInherited', 'Restore inherited tag')}
              aria-label={t('inheritance.restoreInherited', 'Restore inherited tag')}
              data-testid={`${testIdPrefix}.inherited.restore.${tag}`}
            >
              <RotateCcw className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
};
